// src/pages/admin/candidate-status/statusFlags.js
//
// Pure helpers around a candidate status definition.
//
// `isShortlisted` and `isInterview` are gone — migration a1c4e7b93f20 dropped
// both columns, because a status's place in its own flow already says what
// they claimed to. What survives is `isInterviewEligible`: the tenant-
// configurable flag the Interview Scheduling module reads to decide where an
// interview may be booked.

// The canonical frontend shape of a status definition, shared by the admin
// screens and anywhere a full definition (not just an option stub) is needed.
export const normalizeStatusDefinition = (status = {}) => ({
  id: status?.id ?? "",
  code: status?.code ?? "",
  name: status?.name ?? "",
  description: status?.description ?? "",
  sequence: status?.sequence ?? 0,
  isInitial: !!status?.isInitial,
  isHired: !!status?.isHired,
  isRejected: !!status?.isRejected,
  isReusable: !!status?.isReusable,
  isInterviewEligible: !!(status?.isInterviewEligible ?? status?.is_interview_eligible),
});

// Display precedence for the type pill. Terminal roles are the most specific,
// then the reusable waypoint, then the plain middle of the pipeline.
export const getStatusType = (row = {}) => {
  if (row?.isInitial) return "INITIAL";
  if (row?.isHired) return "HIRED";
  if (row?.isRejected) return "REJECTED";
  if (row?.isReusable) return "REUSABLE";
  return "INTERMEDIATE";
};

// A Hired/Rejected status can never receive displaced candidates — the server
// refuses such replacements outright (INVALID_REPLACEMENT_STATUS), so the
// picker filters them up-front instead of waiting for the 409.
export const isTerminalStatus = (status = {}) => !!status?.isHired || !!status?.isRejected;

export const getEligibleReplacementStatuses = (
  statuses = [],
  { excludeId, forInitial = false } = {}
) =>
  (Array.isArray(statuses) ? statuses : [])
    .filter((item) => item?.id && item.id !== excludeId)
    .filter((item) => {
      if (isTerminalStatus(item)) return false;
      // A new initial status must itself be initial-shaped — the same rule
      // the create form enforces — so reusable waypoints stay out too.
      if (forInitial && item?.isReusable) return false;
      return true;
    });

// Question generation is gated on the terminal states alone — exactly what the
// server checks before generating (`ai_tools/router.py`: a status definition
// that is neither hired nor rejected). A button that can only produce a 4xx is
// not a feature, so the two rules stay the same rule.
export const isQuestionGenerationAllowed = (def) => {
  if (!def || typeof def !== "object") return false;
  return !isTerminalStatus(def);
};

export const canGenerateQuestions = ({ statusDef, moduleEnabled, moduleAvailable }) =>
  !!moduleEnabled && !!moduleAvailable && isQuestionGenerationAllowed(statusDef);

// Should the candidate's workflow tabs — Interview and Feedback — be open?
// Both unlock together, on the same rule generation uses; stored data always
// wins, so anything already generated or submitted stays reachable whatever
// the current status says. Without a definition at all (the definitions
// request failed) the caller's past-initial heuristic stands in, so a
// transient outage never hides tabs the reader used to have.
export const areWorkflowTabsUnlocked = ({ statusDef, hasStoredData, pastInitialFallback }) => {
  if (hasStoredData) return true;
  if (statusDef) return isQuestionGenerationAllowed(statusDef);
  return !!pastInitialFallback;
};
